import I18n from 'APP/I18n'
import { decode as atob } from 'base-64'
import { logger } from 'APP/Lib/Datadog'
import { isMobile } from 'APP/Helpers/checkPlatform'
import { getPrimaryLoginStrategy } from 'APP/Lib/ConfigHelpers'

interface ErrorWithStatus {
  status?: string
}

export const getEnrolmentCodeErrorText = (
  showEnrolmentCodeError: boolean,
  showAuthError: boolean,
  error?: Error | ErrorWithStatus | string | null
): string | null => {
  if (showEnrolmentCodeError) {
    return I18n.t('ClaimAccessScreen.error.invalid')
  } else if (showAuthError && error) {
    const status = typeof error === 'object' && 'status' in error ? error.status : undefined
    switch (status) {
      case 'TIMEOUT_ERROR':
      case 'NETWORK_ERROR':
        return I18n.t('Common.error.generic')
      default:
        return I18n.t('ClaimAccessScreen.error.invalid')
    }
  }
  return null
}

export const getExpiration = (expiresIn: number): number => Date.now() + expiresIn * 1000

export const getTokenProperties = (accessToken: string): Record<string, unknown> => {
  try {
    return JSON.parse(atob(accessToken.split('.')[1]))
  } catch (e) {
    logger.error('Failed to decode access token', {}, e)
    return {}
  }
}

export const getCustomClaims = (
  accessToken: string | null | undefined
): Record<string, unknown> => {
  const customClaimPrefix = 'https://dialogue/claims/'
  let tokenProperties: Record<string, unknown> = {}

  if (accessToken) {
    tokenProperties = getTokenProperties(accessToken)
  }

  if (Object.keys(tokenProperties).length === 0) {
    logger.error('Error getting custom claims from access token')
  }

  // Example: {'https://dialogue/claims/random': 'value'} will be returned as {random: 'value'}
  // and all token values not starting with 'https://dialogue/claims/' will be omitted
  return Object.keys(tokenProperties)
    .filter((key) => key.startsWith(customClaimPrefix))
    .reduce(
      (map, key) => ({
        ...map,
        [key.replace(customClaimPrefix, '')]: tokenProperties[key],
      }),
      {}
    )
}

export const shouldResyncWebSessionForUserChange = (
  startupComplete: boolean,
  previousAccessToken: string | null | undefined,
  newAccessToken: string | null | undefined
): boolean => {
  if (isMobile() || !startupComplete || !getPrimaryLoginStrategy()) return false

  const previousUserId = getCustomClaims(previousAccessToken)?.user_id
  const newUserId = getCustomClaims(newAccessToken)?.user_id

  return previousUserId != null && newUserId != null && previousUserId !== newUserId
}

export function isAccessTokenValid(accessToken: string | null | undefined): accessToken is string {
  if (!accessToken) {
    logger.info(
      'No `accessToken` provided to `isAccessTokenValid` helper function. Treating token as invalid.'
    )
    return false
  }

  const { exp: accessTokenExpiration, 'https://dialogue/claims/user_id': accessTokenUserId } =
    getTokenProperties(accessToken)

  if (!accessTokenExpiration) {
    // this is a warning because we always expect this property to exist on the token
    logger.warn(
      'Expiration date has not been verified in `isAccessTokenValid` helper function. Treating token as expired.',
      {
        accessTokenExpiration: accessTokenExpiration,
      }
    )
    return false
  }

  if (!accessTokenUserId) {
    logger.info(
      'Access token does not have a user id in `isAccessTokenValid` helper function. Treating token as invalid.',
      {
        accessTokenUserId: accessTokenUserId,
      }
    )
    return false
  }

  return Number(accessTokenExpiration) * 1000 > Date.now()
}
